import RepositoryInterface from './RepositoryInterface'
import notesServices from './jsonapi'
import {get, omitBy, isNil, isEmpty, range, flatten} from 'lodash'
import {DEFAULT_BULK_SIZE} from '../constants'
import {formatPagination} from '#/notes/utilities/pagination'

/**
 * Private methods declarations
*/
const createSortQuery = Symbol('createSortQuery')
const mapPaginationToRemote = Symbol('mapPaginationToRemote')
const mapPaginationFromRemote = Symbol('mapPaginationFromRemote')
const mapParamsToRemote = Symbol('mapParamsToRemote')

/**
 * Remote servers that use this abstraction should follow
 * the conventions listed here regarding:
 * pagination and ordering field names
 */
export default class RemoteResourceRepository extends RepositoryInterface {
  constructor (resource) {
    super()
    this.modelName = resource
    this.http = notesServices
  }

  /**
   * Creates the ordering query string expected by the remote API
   * @param {Object} ordering object containing order and key
  */
  [createSortQuery] ({key = null, order = null} = {}) {
    if (!key) return null
    return `${order === 'desc' ? '-' : ''}${key}`
  }

  /**
   * Maps the names used on the frontend to those used on the remote API
   * Remote servers are expected to follow the same conventions for pagination fields listed here
   * @param {Object} pagination the pagination object as defined on pagination.js
   * @returns {Object} the new object with the names expected by the remote API
  */
  [mapPaginationToRemote] (pagination) {
    if (isEmpty(pagination)) return null
    return ({
      number: pagination.currentPage,
      size: pagination.pageSize
    })
  }

  /**
   * Maps the names used on the remote API to those used on the frontend
   * @param {Object} pagination the current pagination object that was sent to the endpoint
   * @param {Object} response the response object returned from the API call
   * @returns {Object} the new object with the names expected by the frontend
  */
  [mapPaginationFromRemote] (pagination, response) {
    const meta = response.meta ? response.meta : null

    if (!meta || !meta.pagination) return ({})
    const pageSize = get(pagination, 'pageSize')
    return formatPagination(meta.pagination, pageSize)
  }

  [mapParamsToRemote] ({filters = {}, pagination = {}, sort = {}, ...others} = {}) {
    return omitBy({
      ...others,
      page: this[mapPaginationToRemote](pagination),
      sort: this[createSortQuery](sort),
      ...filters
    }, isNil)
  }

  /**
   * @param {Object} params
   * @param {Object} queries
   * @param {Object} apiMethod
   * @param {Function} method
   * @param {Array} params
   * @returns {Object} {data: Array<Objects>}
   */
  async listAllRecords ({queries = null} = {}) {
    // 1. Fetch first page
    const params = queries ? this[mapParamsToRemote](omitBy(queries, isNil)) : {}
    let response = await this.http.findAll(this.modelName, {
      ...params,
      page: {
        number: 1,
        size: DEFAULT_BULK_SIZE
      }
    })
    const {data, errors, meta, links} = response

    // 2. Populate remaining requests
    // - Return if errors are found.
    if (errors) {
      return {errors}
    }
    // - Short-circuit exit if no further fetching is needed.
    if (meta.pagination.pages === 1) {
      return {data, errors, meta, links}
    }
    // - Populate the remaining requests
    const requests = range(2, meta.pagination.pages + 1).map(page => {
      return this.http.findAll(this.modelName, {
        ...params,
        page: {
          number: page,
          size: DEFAULT_BULK_SIZE
        }
      })
    })
    const responses = await Promise.all(requests)
    // 3. Return aggregated data
    return {
      data: [
        ...response.data,
        ...flatten(responses.map(response => response.data))
      ]
    }
  }

  createRecord ({data}) {
    return this.http.create(this.modelName, data)
  }

  /**
   * @param {Object} params
   * @param {Object} params.queries
   * @returns {Array<Object>}
   */
  async listRecords ({queries = null} = {}) {
    const params = queries ? this[mapParamsToRemote](omitBy(queries, isNil)) : {}

    const response = await this.http.findAll(this.modelName, params)
    const requestPagination = get(queries, 'pagination')
    response.meta.pagination = this[mapPaginationFromRemote](requestPagination, response)
    return response
  }

  readRecord ({id}) {
    return this.http.find(this.modelName, id)
  }

  updateRecord ({id, data}) {
    return this.http.update(this.modelName, {
      ...data,
      id
    })
  }

  deleteRecord ({id}) {
    return this.http.destroy(this.modelName, id)
  }
}
